import axios from 'axios';

const UNSPLASH_KEY = import.meta.env.VITE_UNSPLASH_ACCESS_KEY;
const PEXELS_KEY = import.meta.env.VITE_PEXELS_API_KEY;

/**
 * Helper to get a random item from an array that isn't in the skipUrls list.
 */
function getValidUrl(urls: string[], skipUrls: string[] = []): string | null {
  const validUrls = urls.filter(url => !skipUrls.includes(url));
  if (validUrls.length === 0) return null;
  // Pick randomly from the top available valid ones to add variety but keep relevance
  const topN = Math.min(validUrls.length, 5);
  const randomIndex = Math.floor(Math.random() * topN);
  return validUrls[randomIndex];
}

async function fetchUnsplashImage(query: string, skipUrls: string[] = []): Promise<{ url: string; provider: string } | null> {
  if (!UNSPLASH_KEY) return null;
  try {
    const response = await axios.get('https://api.unsplash.com/search/photos', {
      params: { query, per_page: 15, orientation: 'squarish' },
      headers: { Authorization: `Client-ID ${UNSPLASH_KEY}` }
    });

    if (response.data.results && response.data.results.length > 0) {
      const urls = response.data.results.map((res: any) => res.urls.small);
      const url = getValidUrl(urls, skipUrls);
      if (url) return { url, provider: 'unsplash' };
    }
    return null;
  } catch (error) {
    console.error('Error fetching image from Unsplash:', error);
    return null;
  }
}

async function fetchPexelsImage(query: string, skipUrls: string[] = []): Promise<{ url: string; provider: string } | null> {
  if (!PEXELS_KEY) return null;
  try {
    const response = await axios.get('https://api.pexels.com/v1/search', {
      params: { query, per_page: 15 },
      headers: { Authorization: PEXELS_KEY }
    });

    if (response.data.photos && response.data.photos.length > 0) {
      // Pexels has different sizes, 'medium' is usually good
      const urls = response.data.photos.map((photo: any) => photo.src.medium);
      const url = getValidUrl(urls, skipUrls);
      if (url) return { url, provider: 'pexels' };
    }
    return null;
  } catch (error) {
    console.error('Error fetching image from Pexels:', error);
    return null;
  }
}

async function fetchOpenverseImage(query: string, skipUrls: string[] = []): Promise<{ url: string; provider: string } | null> {
  try {
    const response = await axios.get(`https://api.openverse.org/v1/images/`, {
      params: { q: query, size: 'large', page_size: 15 },
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.data.results && response.data.results.length > 0) {
      const urls = response.data.results.map((res: any) => res.url);
      const url = getValidUrl(urls, skipUrls);
      if (url) return { url, provider: 'openverse' };
    }
    return null;
  } catch (error) {
    console.error('Error fetching image from Openverse:', error);
    return null;
  }
}

export const fetchImageWithFallback = async (query: string, skipUrls: string[] = []): Promise<{ url: string; provider: string } | null> => {
  try {
    const unsplashResult = await fetchUnsplashImage(query, skipUrls);
    if (unsplashResult) return unsplashResult;
  } catch (e) {
    console.warn(`Unsplash fetch failed for "${query}"`);
  }

  try {
    const pexelsResult = await fetchPexelsImage(query, skipUrls);
    if (pexelsResult) return pexelsResult;
  } catch (e) {
    console.warn(`Pexels fetch failed for "${query}"`);
  }

  try {
    const openverseResult = await fetchOpenverseImage(query, skipUrls);
    if (openverseResult) return openverseResult;
  } catch (e) {
    console.warn(`Openverse fetch failed for "${query}"`);
  }
  
  return null;
};
